import React from "react";
import { ArrowLeft, Eraser } from "lucide-react";
import { Link } from "react-router-dom";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import RedisHealthCard from "@/modules/racket_lab_admin/RedisHealthCard";
import DispatchStatusBanner from "@/modules/racket_lab_admin/DispatchStatusBanner";
import JSONInspector from "@/modules/racket_lab_admin/JSONInspector";
import ConfirmActionDialog from "@/modules/racket_lab_admin/ConfirmActionDialog";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminOpsRedisHealth — Sub-sprint D-α + D-β-α.
 *
 * Read-only redis-health snapshot + NP-R1 dispatch status echo.
 * D-β-α adds the T1 Rate-limit reset action (in-memory only,
 * confirmation-protected).
 */
const RLAdminOpsRedisHealth = () => {
  const t = useRacketLabAdminT();
  const { data, loading, error, reload } = useAdminApi(
    "/admin/qr-tokens/_ops/redis-health",
  );
  const { run, loading: mutating } = useAdminMutation();
  const [resetResult, setResetResult] = React.useState(null);

  const onReset = async () => {
    const r = await run("post", "/admin/qr-tokens/_ops/rate-limit-reset", {});
    setResetResult(r);
    reload();
  };

  return (
    <div data-testid="rl-admin-ops-redis-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />
      <DispatchStatusBanner />

      <Link
        to="/admin/racket-lab/ops"
        data-testid="rl-ops-redis-back"
        className="inline-flex w-fit items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("ops.common.back_to_toolkit")}
      </Link>

      <header>
        <h1 className="text-xl font-semibold text-slate-900">{t("ops.redis.page_title")}</h1>
        <p className="text-xs text-slate-500">{t("ops.redis.subtitle")}</p>
      </header>

      <RedisHealthCard data={data} loading={loading} error={error} dataTestId="rl-ops-redis-card" />

      {/* Sub-sprint D-β-α — Rate-limit reset (T1). */}
      <section data-testid="rl-ops-rate-limit-reset-section" className="flex flex-col gap-2 rounded-md border border-slate-200 bg-white p-4">
        <header className="flex items-center justify-between gap-3">
          <div>
            <h2 className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-800">
              <Eraser className="w-4 h-4 text-amber-600" /> {t("ops.redis.rate_limit_reset_title")}
            </h2>
            <p className="text-[11px] text-slate-500">
              {t("ops.redis.rate_limit_reset_subtitle")}
            </p>
          </div>
          <ConfirmActionDialog
            dataTestId="rl-ops-rate-limit-reset-confirm"
            trigger={
              <button
                type="button"
                data-testid="rl-ops-rate-limit-reset-btn"
                disabled={mutating}
                className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:bg-amber-300"
              >
                <Eraser className="w-3.5 h-3.5" /> {t("ops.redis.reset_buckets_btn")}
              </button>
            }
            title={t("ops.redis.reset_dialog_title")}
            description={t("ops.redis.reset_dialog_description")}
            confirmLabel={t("ops.redis.reset")}
            onConfirm={onReset}
            disabled={mutating}
          />
        </header>
        {resetResult && (
          <div data-testid="rl-ops-rate-limit-reset-result" className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-900">
            {t("ops.redis.reset_ok_at", { at: resetResult.at })}
          </div>
        )}
      </section>

      {data && (
        <section data-testid="rl-ops-redis-json-section">
          <h2 className="mb-2 text-sm font-medium text-slate-700">{t("ops.redis.full_snapshot")}</h2>
          <JSONInspector value={data} dataTestId="rl-ops-redis-json" />
        </section>
      )}
    </div>
  );
};

export default RLAdminOpsRedisHealth;
